import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox
} from "@mui/material";

interface NavigationRow {
  topNav: string;
  leftNav: string;
  viewLabel: string;
  configName: string;
  viewKey?: string;
  topDown?: boolean;
  bottomUp?: boolean;
}

interface NavigationPanelProps {
  uiConfig: NavigationRow[];
  selectedTopNav: string;
  setSelectedTopNav: (value: string) => void;
  selectedLeftNav: string;
  setSelectedLeftNav: (value: string) => void;
  selectedView: string;
  setSelectedView: (value: string) => void;
  selectedConfig: string;
  setSelectedConfig: (value: string) => void;
  onSetIsTopDown: (value: boolean) => void;
  onSetIsBottomUp: (value: boolean) => void;
}

function NavigationPanel({
  uiConfig,
  selectedTopNav,
  setSelectedTopNav,
  selectedLeftNav,
  setSelectedLeftNav,
  selectedView,
  setSelectedView,
  selectedConfig,
  setSelectedConfig,
  onSetIsTopDown,
  onSetIsBottomUp
}: NavigationPanelProps) {
  /* ---------------------------------
     Helpers that filter the nav rows
     ---------------------------------*/
  const topNavItems = useMemo(
    () => Array.from(new Set(uiConfig.map(r => r.topNav).filter(Boolean))),
    [uiConfig]
  );

  const topNavRows = useMemo(
    () => uiConfig.filter(r => r.topNav === selectedTopNav),
    [uiConfig, selectedTopNav]
  );

  const leftNavItems = useMemo(
    () => Array.from(new Set(topNavRows.map(r => r.leftNav).filter(Boolean))),
    [topNavRows]
  );

  const leftNavRows = useMemo(
    () => topNavRows.filter(r => r.leftNav === selectedLeftNav),
    [topNavRows, selectedLeftNav]
  );

  const views = useMemo(
    () => Array.from(new Set(leftNavRows.map(r => r.viewLabel))),
    [leftNavRows]
  );

  const configs = useMemo(
    () => Array.from(new Set(leftNavRows
      .filter(r => r.viewLabel === selectedView)
      .map(r => r.configName))),
    [leftNavRows, selectedView]
  );

  /* ----------------------------------------------------------
     When all 4 nav levels are chosen find the single row
     ----------------------------------------------------------*/
  const matchingRow = useMemo(
    () =>
      uiConfig.find(r =>
        r.topNav    === selectedTopNav  &&
        r.leftNav   === selectedLeftNav &&
        r.viewLabel === selectedView    &&
        r.configName=== selectedConfig
      ),
    [uiConfig, selectedTopNav, selectedLeftNav, selectedView, selectedConfig]
  );

  /* always update Top‑Down flag when matching row changes */
  useEffect(() => {
    onSetIsTopDown(!!matchingRow?.topDown);
  }, [matchingRow, onSetIsTopDown]);

  useEffect(() => {
    onSetIsBottomUp(!!matchingRow?.bottomUp);
  }, [matchingRow, onSetIsBottomUp]);

  /* -------------  render  ------------- */
  return (
    <Box sx={{ p: 2 }}>
      {/* Top Nav selector */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Top Nav</InputLabel>
        <Select
          value={selectedTopNav}
          label="Top Nav"
          onChange={e => setSelectedTopNav(e.target.value)}
        >
          {topNavItems.map(nav => (
            <MenuItem key={nav} value={nav}>{nav}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Left Nav selector */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Left Nav</InputLabel>
        <Select
          value={selectedLeftNav}
          label="Left Nav"
          onChange={e => setSelectedLeftNav(e.target.value)}
        >
          {leftNavItems.map(ln => (
            <MenuItem key={ln} value={ln}>{ln}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* View selector */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>View</InputLabel>
        <Select
          value={selectedView}
          label="View"
          onChange={e => setSelectedView(e.target.value)}
        >
          {views.map(v => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Config selector */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Config</InputLabel>
        <Select
          value={selectedConfig}
          label="Config"
          onChange={e => setSelectedConfig(e.target.value)}
        >
          {configs.map(c => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Top‑Down checkbox (Bottom‑Up is read‑only true/false) */}
      {matchingRow && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Mode:</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!matchingRow.topDown}
                onChange={e => onSetIsTopDown(e.target.checked)}
              />
            }
            label="Top‑Down"
          />
          <FormControlLabel
            control={<Checkbox checked={!!matchingRow.bottomUp} disabled />}
            label="Bottom‑Up"
          />
        </Box>
      )}
    </Box>
  );
}


export default NavigationPanel;
